import {
  AllServiceRequestsResponse,
  GetUniqueFilterValuesRequestBase,
  GetServiceRequestPicklistCategoriesRequest,
  ServiceRequestClient,
  ServiceRequestDTO,
  ServiceReportsFilterRequest,
  ServiceRequestListDocumentDto,
  ServiceRequestResponse,
  ServiceRequestSummaryDTO,
  ServiceReportsOverviewResponse,
  UniqueFilterValuesDto,
  ServiceRequestDetailsDto,
  ServiceRequestContactCandidateDto,
  ServiceRequestContactSelectionDto,
  ServiceRequestMessageDto,
  ServiceRequestDownloadRequest,
  FileDownloadResponse,
  ServiceRequestIssueRankingsWidgetDto,
  RequesterRankingsVisualsDto,
  SurveyMetricsResponse,
  ServiceReportExportRequest,
  ServiceReportExportTabIdentifier,
  UpdateServiceRequestContactsResponse,
} from "lib/ShiOneClient";
import type { IncludeExcludeFilterCategory } from "shared-ui/src/components/include-exclude-filter/reducers/includeExcludeFilterReducer";
import { ApiClientConfig } from "../auth/ApiClientConfig";

const api = new ServiceRequestClient(
  ApiClientConfig,
  import.meta.env.API_ENDPOINT,
);

export async function getRestAllServiceRequests(
  request: AllServiceRequestsResponse,
): Promise<AllServiceRequestsResponse> {
  return api.getAllAccountServiceRequests(request);
}

export async function getRestMyServiceRequests(): Promise<ServiceRequestDTO[]> {
  return api.getMyServiceRequests();
}

export async function getRestMyAdditionalContactRequests(): Promise<
  ServiceRequestDTO[]
> {
  return api.getMyAdditionalContactServiceRequests();
}

export async function getRestServiceRequestById(
  id: string,
): Promise<ServiceRequestDTO> {
  return api.getServiceRequestByServiceRequestId(id);
}

export async function postRestNewServiceRequest(
  request: ServiceRequestDTO,
): Promise<ServiceRequestResponse> {
  return api.createNewServiceRequest(request);
}

export async function putRestUpdateServiceRequestById(
  request: ServiceRequestDTO,
): Promise<ServiceRequestResponse> {
  return api.updateServiceRequest(request.id as number, request);
}

export async function getRequestsSummary(): Promise<ServiceRequestSummaryDTO> {
  return api.summary();
}

export async function getElasticServiceRequests(
  queryRequest: ServiceReportsFilterRequest,
): Promise<ServiceRequestListDocumentDto> {
  if (queryRequest.pageSize === 0) {
    // @ts-ignore
    return { totalCount: 0, serviceRequestDtos: [] };
  }

  return api.searchServiceRequests(queryRequest);
}

export async function getServiceRequestDownload(
  downloadRequest: ServiceRequestDownloadRequest,
): Promise<FileDownloadResponse> {
  downloadRequest.queryString = JSON.stringify(downloadRequest.query);
  downloadRequest.query = undefined;
  return api.serviceRequestsDownload(downloadRequest);
}

export async function getElasticServiceRequestsUniqueFilterValues(
  queryRequest: GetUniqueFilterValuesRequestBase,
): Promise<UniqueFilterValuesDto> {
  if (queryRequest.query?.pageSize === 0) {
    // @ts-ignore
    return { uniqueAssignedToValues: [], uniqueCreatedByValues: [] };
  }

  return api.uniqueFilterValues(queryRequest);
}

export async function getServiceRequestPicklistCategories(
  queryRequest: GetUniqueFilterValuesRequestBase,
): Promise<IncludeExcludeFilterCategory[]> {
  // Convert GetUniqueFilterValuesRequestBase to GetServiceRequestPicklistCategoriesRequest
  const picklistRequest = new GetServiceRequestPicklistCategoriesRequest({
    query: queryRequest.query,
    contactIds: [],
    serviceRequestQueryType: (queryRequest as any).serviceRequestQueryType,
  });

  const response = await api.getPicklistCategories(picklistRequest);
  return (response.includeExcludeFilterCategories ||
    []) as IncludeExcludeFilterCategory[];
}

export async function getServiceRequestDetails(
  serviceRequestId: string,
): Promise<ServiceRequestDetailsDto> {
  return api.serviceRequestDetails(serviceRequestId);
}

export async function getServiceRequestContactCandidates(
  accountId: number,
): Promise<ServiceRequestContactCandidateDto[]> {
  return api.getServiceRequestContactCandidates(accountId);
}

export async function postServiceRequestAdditionalContacts(
  serviceRequestId: string,
  selections: ServiceRequestContactSelectionDto[],
): Promise<UpdateServiceRequestContactsResponse> {
  return api.updateAdditionalContacts(serviceRequestId, selections);
}

export async function postServiceRequestMessage(
  message: ServiceRequestMessageDto,
) {
  return api.addServiceRequestMessage(message);
}

export async function postServiceRequestPreferredContactMethod(
  serviceRequestId: number,
  preferredContactMethod: string,
) {
  return api.updatePreferredContactMethod(
    serviceRequestId,
    preferredContactMethod,
  );
}

export async function getServiceReportsOverview(
  filter: ServiceReportsFilterRequest,
): Promise<ServiceReportsOverviewResponse> {
  return api.getServiceReportsOverview(filter);
}

export async function getServiceReportsRequesterRankings(
  filter: ServiceReportsFilterRequest,
): Promise<RequesterRankingsVisualsDto> {
  return api.getServiceReportsRequesterRankings(filter);
}

export async function getServiceReportIssueRankings(
  filter: ServiceReportsFilterRequest,
): Promise<ServiceRequestIssueRankingsWidgetDto> {
  return api.getServiceReportsIssueRankings(filter);
}

export async function getSurveyMetrics(
  filter: ServiceReportsFilterRequest,
): Promise<SurveyMetricsResponse> {
  return api.getSurveyMetrics(filter);
}

export interface ServiceReportExportRequestPayload {
  tabIdentifier: ServiceReportExportTabIdentifier;
  filters: ServiceReportsFilterRequest;
}

export interface ServiceReportExportResponse {
  data: Blob;
  fileName?: string;
}

export async function postServiceReportExport(
  payload: ServiceReportExportRequestPayload,
): Promise<ServiceReportExportResponse> {
  const response = await api.exportServiceReport(
    ServiceReportExportRequest.fromJS(payload),
  );

  return {
    data: response.data,
    fileName: response.fileName,
  };
}
